<template>
  <div class="MagentoEditor">
    <input v-if="showFile" type="file" ref="editor-upload" accept="image/*" style="display: none" @change="change" multiple>

    <div class="iframe-wrapper">
      <iframe :class="{moving}"
        :width="width"
        :height="height"
        :src="fullDomainLink + '?preview=true'"

        loading="eager"
        ref="iframe"
        id="iframe"
      ></iframe>

      <transition name="fade-trans">
        <div v-if="!loaded || error" class="loading-wrapper">
          <template v-if="!error">
            <svg class="text-primary animate-spin" width="60" height="60" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle style="opacity: .25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path style="opacity: .75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <h2>
              Loading website editor...
            </h2>
          </template>
          <template v-else>
            <h2>
              There was an error when loading the website
            </h2>
            <p>
              It either has an error or hasn't been set up properly.
            </p>
          </template>
        </div>
      </transition>
    </div>
    <div class="sidebar-wrapper d-flex" :style="{flexBasis: clientWidth + 'px'}">
      <div class="line" ref="handle"></div>

      <Sidebar v-if="domainName"
        :fullDomainLink="fullDomainLink"
        :config="config"
        :currentClientWidth="currentClientWidth"
        :selectedLang='selectedLang'

        @set-lang="lang => selectedLang = lang"
        @input="setConfig"
        @save="saveConfig"
        @remove-path="removePath"
        @refetch-website='$emit("refetch-website")'

        @on-save="onSave"
        @remove-on-save="removeOnSave"
      />
      <div class="bottom-header d-flex justify-content-end" @click="saveConfig">
        <button class="btn btn-primary">Save configuration</button>
      </div>
    </div>
  </div>
</template>

<script>

import resize from "./resize"

import Sidebar from "./Sidebar/Sidebar.vue"

export function isObject(item) {
  return (item && typeof item === 'object' && !Array.isArray(item));
}
const getPathReference = (obj, path) => {
  const splitted = path.split(".")
  if (splitted.length === 1) return obj[path]
    const key = splitted.shift()
    return getPathReference(obj[key], splitted.join('.'))
}

const unmappObject = maps => {
  const result = {}
  const paths = Object.keys(maps).filter(key => maps[key])

  const mapContentToPath = (path, target, value, previousTarget, previousKey) => {
    if (!path.length || !target) return;
    let key = path[0]

    const next = (nextTarget = target[key]) => {
      const previousKey = path.shift()
      mapContentToPath(path, nextTarget, value, target, previousKey)
    }
    const isArr = someKey => /\[\d\]/.test(someKey + '')

    if (path.length === 1) {
      target[key] = value
    } else if (isArr(key)) {
    	const index = parseInt(key.substring(1), 10)
        if (!previousTarget[previousKey] || !Array.isArray(previousTarget[previousKey])) {
          if (!isArr(previousKey)) previousTarget[previousKey] = []
        }

      if (!previousTarget[previousKey][index]) previousTarget[previousKey].push({})

      return next(previousTarget[previousKey][index])
    } else {
      target[key] = Array.isArray(target[key]) ? [...target[key]] : {...target[key]}
    }

    next()
  }

  for (const path of paths) {
  	mapContentToPath(path.split(/(\.)|(\[\d\])/).filter(item => item && item !== '.'), result, maps[path])
  }

  return result
}

export default {
  components: {
    Sidebar,
  },
  mixins: [resize],
  props: {
    domainName: String,
    websiteUid: String,
    initialConfig: String,
    wholesale: String,
  },
  data() {
    return {
      height: 0,
      showFile: true,
      currentClientWidth: 0,
      loaded: false,
      error: false,
      preventMagentoUpdate: false,
      selectedLang: "default",
      saveListeners: [],

      config: {
        target: null,
        tailwindConfig: null,
        blocks: [],
      },
    }
  },
  created() {
    const endpoints = [
      {endpoint: `/api/websites/${this.websiteUid}/pages`, params: { params: {
        page: 1,
        page_size: 10,

        name: "",
        type: "page",
      } }},
    ]

    endpoints.forEach(param => this.$store.dispatch("keepTrackOfEndpoint", param))

    this.timeout = setTimeout(() => {
      this.error = true
    }, 20000)

    if (this.initialConfig) this.setConfig(JSON.parse(this.initialConfig), false)

    // Event from iframe magento.
    window.addEventListener("message", evt => {
      if (evt.origin !== this.fullDomainLink) return;

      if (evt.data === "magento-content-loaded") {
        clearTimeout(this.timeout)
        this.error = false
        this.getWebsiteImages()
        this.updateMagento()
      } else if (evt.data === "magento-config-rendered") {
        this.loaded = true
      } else if (evt.data === "request-file-upload") {
        this.$refs["editor-upload"].click()
      } else {
        this.preventMagentoUpdate = true
        const {block, content, keysToRemove} = evt.data

        const config = this.setConfig({
          content: {
            [block]: unmappObject(content)
          },
        })
        keysToRemove && keysToRemove.forEach(key => {
          const splitted = key.split(".")
          const index = splitted.pop()
          const ref = getPathReference(config.content[block], splitted.join("."))
          delete ref[index]

          if (config.content[block]['nl_NL']) {
            const dutchRef = getPathReference(config.content[block]['nl_NL'], splitted.join("."))
            if (dutchRef) delete dutchRef[index]
          }
        })
      }
    })
  },
  beforeDestroy() {
    if (this.timeout) clearTimeout(this.timeout)
  },
  mounted() {
    this.initialConfig && this.setConfig(this.config)

    this.calculateHeight()
    window.addEventListener("resize", this.calculateHeight)
  },
  methods: {
    change(evt) {
      const files = [...this.$refs["editor-upload"].files]
      this.$handlePromise(async () => {
        await Promise.all(
          files.map(async file => {
            const payload = new FormData()
            payload.append("filename", file)
            payload.append("type", "frontpage_01")

            await this.$handlePromise(
              this.axios.post(`/api/websites/${this.websiteUid}/files`, payload, {
                headers: {
                  "Content-Type": "multipart/form-data"
                }
              })
            )
            this.$store.dispatch('clearCacheSearch', "websites")
          })
        )

        await this.getWebsiteImages()
        this.$success("Bestanden succesvol geüpload!")
      })
    },
    onSave(func) {
      if (this.saveListeners.indexOf(func) < 0) this.saveListeners.push(func)
    },
    removeOnSave(func) {
      const index = this.saveListeners.indexOf(func)
      if (index > -1) this.saveListeners.splice(index, 1)
    },
    removePath(path, obj = this.config) {
      const splitted = path.split(".")
      const index = splitted.pop()
      const ref = getPathReference(obj, splitted.join("."))
      delete ref[index]
    },
    async getWebsiteImages() {
      const {results} = (await this.axios.get(`/api/websites/${this.websiteUid}/files`, {params: {page_size: 50}})).data
      this.sendDynamicData({ availableImages: results.map(item => item.url) })
    },
    sendDynamicData(data) {
      data.__dynamic = true
      this.$refs.iframe.contentWindow.postMessage(data, "*")
    },
    async saveConfig() {
      const config = {...this.config}
      delete config.target
      delete config.event_bus

      await this.$handlePromise(async () => {
        await Promise.all([
          ...this.saveListeners.map(func => func()),
          this.axios.put(`/api/websites/${this.websiteUid}`, {
            properties: {
              wholesale: this.wholesale,
            },
            magento_config: JSON.stringify(config)
          })
        ])
        this.$store.dispatch('clearCacheSearch', "websites")

        this.$success('Successfully updated website config!')
      })
    },

    setConfig(newConfig, updateMagento = true) {
      this.config = {...this.mergeDeep(this.config, newConfig)}
      updateMagento && this.updateMagento(newConfig)
      return this.config
    },
    mergeDeep(target, ...sources) {
      if (!sources.length) return target;
      const source = sources.shift();

      if (isObject(target) && isObject(source)) {
        for (const key in source) {
          if (isObject(source[key])) {
            if (!target[key]) Object.assign(target, { [key]: {} });
            this.mergeDeep(target[key], source[key]);
          } else {
            Object.assign(target, { [key]: source[key] });
          }
        }
      }

      return this.mergeDeep(target, ...sources);
    },
    calculateHeight() {
      this.height = this.$el.parentNode.getBoundingClientRect().height
    },
    updateMagento(newConfig = this.config) {
      if (this.preventMagentoUpdate) return this.preventMagentoUpdate = false
      this.$refs.iframe.contentWindow.postMessage({
        ...newConfig,
        lang: this.selectedLang === "default" ? "english" : this.selectedLang,
      }, "*")
    },
  },
  computed: {
    fullDomainLink() {
      return "https://" + this.domainName
    },
  },
  watch: {
    selectedLang() {
      this.updateMagento({})
    },
  },
}

</script>

<style scoped>

p {
  font-size: 14px;
  max-width: 300px;
  text-align: center;
}

.bottom-header {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 100%;
  border-top: 1px solid #e3e3e3;
  background-color: white;
  padding: 12px;
  flex-grow: unset;
  flex-shrink: 0;
}

.MagentoEditor {
  display: flex;
  position: relative;
}

.moving {
  pointer-events: none;
}

.loading-wrapper {
  position: absolute;
  left: 0;
  top: 0;
  background-color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  border-right: 1px solid rgba(100,100,100,.2);
}

.animate-spin {
  animation: spin 1s linear infinite;
}

h2 {
  font-weight: 700;
  font-size: 16px;
}

.sidebar-wrapper {
  position: relative;
  flex-shrink: 0;
}

.iframe-wrapper {
  position: relative;
}

.line {
  position: absolute;
  left: 0;
  top: 0;
  width: 5px;
  transform: translateX(-2.5px);
  height: 100%;
  cursor: grab;
  opacity: 0;
  background-color: black;
  transition-duration: .15s;
}

.line:hover {
  width: 10px;
  opacity: .1;
  transform: translateX(-5px);
}

iframe {
  border: none;
  border-right: 1px solid rgba(100,100,100,.2);
}

@keyframes spin{to{transform:rotate(1turn)}}.animate-spin{animation:spin 1s linear infinite}@keyframes ping{75%,to{transform:scale(2);opacity:0}}.animate-ping{animation:ping 1s cubic-bezier(0,0,.2,1) infinite}@keyframes pulse{50%{opacity:.5}}.animate-pulse{animation:pulse 2s cubic-bezier(.4,0,.6,1) infinite}

</style>
